import { useEffect, useRef } from 'react';

import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Toast,
} from '@repo/videa-components';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useConfigurePractice,
  useGetPracticeConfiguration,
} from '@/react-query/rcm/usePracticeConfiguration';
import routes from '@/routes/routes.constants';

import {
  type PracticeConfigurationFormValues,
  createPracticeConfigurationSchema,
} from './schema';

const INITIALIZED_WITHOUT_ID = Symbol('initialized-from-config-without-id');

export const PracticeConfigurationSettingsPage = () => {
  const history = useHistory();
  const { t } = useLingui();
  const { data: configuration, isLoading } = useGetPracticeConfiguration();
  const configureMutation = useConfigurePractice();

  const form = useForm<PracticeConfigurationFormValues>({
    resolver: zodResolver(createPracticeConfigurationSchema()),
    defaultValues: {
      taxIdentificationNumber: '',
      nationalProviderId: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipcode: '',
    },
  });

  // Track what we last initialized from (id, or INITIALIZED_WITHOUT_ID when config had no id) to prevent infinite loops
  const lastInitializedConfigId = useRef<
    number | null | typeof INITIALIZED_WITHOUT_ID
  >(null);

  // Update form data when configuration loads. Fills form even when configuration has no id.
  // Dependencies are configuration?.id and !!configuration only (not configuration itself) to prevent
  // infinite loops when React Query returns new object references with the same data.
  useEffect(() => {
    const shouldInitialize =
      configuration &&
      (lastInitializedConfigId.current === null ||
        (configuration.id != null &&
          configuration.id !== lastInitializedConfigId.current) ||
        (configuration.id == null &&
          lastInitializedConfigId.current !== INITIALIZED_WITHOUT_ID));
    if (shouldInitialize) {
      lastInitializedConfigId.current =
        configuration.id ?? INITIALIZED_WITHOUT_ID;
      form.reset({
        taxIdentificationNumber: configuration.taxIdentificationNumber || '',
        nationalProviderId: configuration.nationalProviderId || '',
        address1: configuration.address1 || '',
        address2: configuration.address2 || '',
        city: configuration.city || '',
        state: configuration.state || '',
        zipcode: configuration.zipcode || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps to avoid loops
  }, [configuration?.id, !!configuration]);

  const address1Value = form.watch('address1');

  const handleCopyAddress1ToAddress2 = () => {
    form.setValue('address2', address1Value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = async (values: PracticeConfigurationFormValues) => {
    try {
      await configureMutation.mutateAsync({
        taxIdentificationNumber: values.taxIdentificationNumber,
        nationalProviderId: values.nationalProviderId,
        address1: values.address1,
        address2: values.address2 || null,
        city: values.city,
        state: values.state,
        zipcode: values.zipcode,
      });
      toast(
        <Toast
          variant="success"
          title={t`Settings saved successfully`}
          description={t`Practice configuration has been updated.`}
        />,
      );
    } catch (error) {
      toast(
        <Toast
          variant="error"
          title={t`Failed to save settings`}
          description={t`Please try again.`}
        />,
      );
    }
  };

  const handleBack = () => {
    history.push(routes.CONNECT_PAYERS);
  };

  const requiredMark = <span className="vai-text-red-500 vai-ml-1">*</span>;

  return (
    <div className="vai-flex vai-flex-col vai-h-screen vai-w-full vai-overflow-hidden">
      {/* Topbar */}
      <div className="vai-px-6 vai-py-5 vai-bg-white vai-border-b vai-text-primary vai-flex vai-justify-between vai-items-center vai-flex-shrink-0">
        <button
          onClick={handleBack}
          className="vai-flex vai-items-center vai-gap-2 vai-text-primary hover:vai-opacity-80 vai-transition-opacity"
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="vai-mr-2 vai-text-primary"
          />
          <span>
            <Trans>Back to AutoVerify Credentials</Trans>
          </span>
        </button>
      </div>

      {/* Page Content */}
      <div className="vai-flex-1 vai-overflow-auto vai-bg-gray-50 vai-flex vai-flex-col vai-min-h-0">
        <div className="vai-p-6 vai-flex-shrink-0">
          {/* Title Section */}
          <div>
            <h1 className="vai-text-2xl vai-font-semibold vai-text-gray-900 vai-mb-2">
              <Trans>Practice Configuration Settings</Trans>
            </h1>
            <p className="vai-text-sm vai-text-gray-600">
              <Trans>
                Manage your practice configuration settings for AutoVerify.
              </Trans>
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="vai-flex-1 vai-overflow-auto vai-px-6 vai-pb-6 vai-min-h-0">
          <div className="vai-max-w-2xl">
            <div className="vai-bg-white vai-rounded-lg vai-p-6 vai-border vai-border-border">
              {isLoading ? (
                <div className="vai-space-y-4">
                  <div className="vai-animate-pulse">
                    <div className="vai-h-4 vai-bg-gray-200 vai-rounded vai-w-1/4 vai-mb-2"></div>
                    <div className="vai-h-10 vai-bg-gray-200 vai-rounded"></div>
                  </div>
                </div>
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="vai-space-y-6"
                  >
                    {/* Tax Identification Number and National Provider ID */}
                    <div className="vai-grid vai-grid-cols-2 vai-gap-4">
                      <FormField
                        control={form.control}
                        name="taxIdentificationNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              <Trans>Tax Identification Number</Trans>
                              {requiredMark}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t`Enter tax identification number`}
                                disabled={configureMutation.isLoading}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="nationalProviderId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              <Trans>National Provider ID</Trans>
                              {requiredMark}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t`Enter national provider ID`}
                                disabled={configureMutation.isLoading}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Address Line 1 */}
                    <FormField
                      control={form.control}
                      name="address1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            <Trans>Address Line 1</Trans>
                            {requiredMark}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t`Enter address line 1`}
                              disabled={configureMutation.isLoading}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Address Line 2 (optional) */}
                    <FormField
                      control={form.control}
                      name="address2"
                      render={({ field }) => (
                        <FormItem>
                          <div className="vai-flex vai-items-center vai-justify-between">
                            <FormLabel>
                              <Trans>Address Line 2</Trans>
                            </FormLabel>
                            {address1Value && (
                              <button
                                type="button"
                                onClick={handleCopyAddress1ToAddress2}
                                className="vai-text-sm vai-text-blue-600 hover:vai-text-blue-800 vai-underline vai-cursor-pointer"
                                disabled={configureMutation.isLoading}
                              >
                                <Trans>Copy from Address Line 1</Trans>
                              </button>
                            )}
                          </div>
                          <FormControl>
                            <Input
                              placeholder={t`Enter address line 2`}
                              disabled={configureMutation.isLoading}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* City, State, Zipcode */}
                    <div className="vai-grid vai-grid-cols-3 vai-gap-4">
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              <Trans>City</Trans>
                              {requiredMark}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t`Enter city`}
                                disabled={configureMutation.isLoading}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="state"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              <Trans>State</Trans>
                              {requiredMark}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t`Enter state`}
                                disabled={configureMutation.isLoading}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="zipcode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              <Trans>Zipcode</Trans>
                              {requiredMark}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t`Enter zipcode`}
                                disabled={configureMutation.isLoading}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Save Button */}
                    <div className="vai-flex vai-justify-end vai-pt-2">
                      <Button
                        type="submit"
                        disabled={
                          configureMutation.isLoading || !form.formState.isDirty
                        }
                        className="vai-min-w-[120px]"
                      >
                        {configureMutation.isLoading ? (
                          <Trans>Saving...</Trans>
                        ) : (
                          <Trans>Save Changes</Trans>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
